"use client";

/**
 * SocialProofPanel Component
 *
 * Right-side panel for social proof during onboarding.
 * Matches the auth pages design exactly:
 * - Pink rounded card with white padding frame
 * - Flickering grid background
 * - Rotating testimonial cards
 * - Avatar group with trust stat
 * - Google reviews badge
 * - Logo carousel
 */
import { c as _c } from "react/compiler-runtime";
import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import Image from "next/image";
import Anchor from "@shared-ui/components/Anchor";
import { T } from "gt-next";
import { AvatarGroup, AvatarGroupItem, AvatarGroupTooltip, AvatarGroupYou, SimpleAvatar } from "@/components/ui/avatar-group";
import { FlickeringGrid } from "@/components/ui/flickering-grid";
import LandingLogosCarousel from "@/components/landing/LandingLogosCarousel";
import { getFeaturedClients } from "@/utils/clients";

// Generate testimonials from centralized client data
const TESTIMONIALS = getFeaturedClients().filter(c => c.testimonial?.quote).slice(0, 4).map((client, index) => {
  const growth = client.metrics ? `+${Math.round((client.metrics.followersAfter - client.metrics.followersBefore) / 1000)}K followers` : "+10K followers";
  return {
    id: index + 1,
    quote: client.testimonial!.quote,
    name: client.name,
    role: client.niche.charAt(0).toUpperCase() + client.niche.slice(1) + " Creator",
    avatarUrl: client.avatar,
    growth: growth
  };
});

// Generate avatar data from same clients
const AVATARS = TESTIMONIALS.map(testimonial => ({
  src: testimonial.avatarUrl,
  name: testimonial.name.split(" ")[0] + " " + (testimonial.name.split(" ")[1]?.[0] || "") + ".",
  fallback: testimonial.name.split(" ").map(n => n[0]).join("").slice(0, 2).toUpperCase(),
  growth: testimonial.growth
}));
interface SocialProofPanelProps {
  className?: string;
}
export default function SocialProofPanel(t0) {
  const $ = _c(53);
  const {
    className: t1
  } = t0;
  const className = t1 === undefined ? "" : t1;
  const [currentTestimonialIndex, setCurrentTestimonialIndex] = useState(0);
  let t2;
  let t3;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = () => {
      const interval = setInterval(() => {
        setCurrentTestimonialIndex(_temp);
      }, 6000);
      return () => clearInterval(interval);
    };
    t3 = [];
    $[0] = t2;
    $[1] = t3;
  } else {
    t2 = $[0];
    t3 = $[1];
  }
  useEffect(t2, t3);
  const currentTestimonial = TESTIMONIALS[currentTestimonialIndex];
  const t4 = `relative flex bg-white p-3 xl:p-4 ${className}`;
  let t5;
  let t6;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = {
      backgroundColor: "#ffa3d1"
    };
    t6 = <FlickeringGrid className="absolute inset-0" squareSize={3} gridGap={24} color="#ffffff" maxOpacity={0.35} flickerChance={0.04} squareRound={1.5} />;
    $[2] = t5;
    $[3] = t6;
  } else {
    t5 = $[2];
    t6 = $[3];
  }
  let t7;
  let t8;
  let t9;
  if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = {
      opacity: 0,
      y: 20,
      scale: 0.98
    };
    t8 = {
      opacity: 1,
      y: 0,
      scale: 1
    };
    t9 = {
      opacity: 0,
      y: -20,
      scale: 0.98
    };
    $[4] = t7;
    $[5] = t8;
    $[6] = t9;
  } else {
    t7 = $[4];
    t8 = $[5];
    t9 = $[6];
  }
  let t10;
  if ($[7] === Symbol.for("react.memo_cache_sentinel")) {
    t10 = {
      duration: 0.4,
      ease: [0.4, 0, 0.2, 1]
    };
    $[7] = t10;
  } else {
    t10 = $[7];
  }
  let t11;
  if ($[8] !== currentTestimonial.quote) {
    t11 = <p className="mb-5 text-[17px] font-medium leading-[1.6] tracking-[-0.01em] text-ink">"{currentTestimonial.quote}"</p>;
    $[8] = currentTestimonial.quote;
    $[9] = t11;
  } else {
    t11 = $[9];
  }
  let t12;
  if ($[10] !== currentTestimonial.avatarUrl || $[11] !== currentTestimonial.name) {
    t12 = <Image src={currentTestimonial.avatarUrl} alt={currentTestimonial.name} width={44} height={44} className="rounded-full object-cover ring-2 ring-ink/10" />;
    $[10] = currentTestimonial.avatarUrl;
    $[11] = currentTestimonial.name;
    $[12] = t12;
  } else {
    t12 = $[12];
  }
  let t13;
  if ($[13] !== currentTestimonial.name) {
    t13 = <p className="text-[14px] font-semibold text-ink">{currentTestimonial.name}</p>;
    $[13] = currentTestimonial.name;
    $[14] = t13;
  } else {
    t13 = $[14];
  }
  let t14;
  if ($[15] !== currentTestimonial.role) {
    t14 = <p className="text-[12px] text-ink/50">{currentTestimonial.role}</p>;
    $[15] = currentTestimonial.role;
    $[16] = t14;
  } else {
    t14 = $[16];
  }
  let t15;
  if ($[17] !== t13 || $[18] !== t14) {
    t15 = <div>{t13}{t14}</div>;
    $[17] = t13;
    $[18] = t14;
    $[19] = t15;
  } else {
    t15 = $[19];
  }
  let t16;
  if ($[20] !== t12 || $[21] !== t15) {
    t16 = <div className="flex items-center gap-3">{t12}{t15}</div>;
    $[20] = t12;
    $[21] = t15;
    $[22] = t16;
  } else {
    t16 = $[22];
  }
  let t17;
  if ($[23] === Symbol.for("react.memo_cache_sentinel")) {
    t17 = <svg className="h-3.5 w-3.5 text-emerald-500" viewBox="0 0 20 20" fill="currentColor"><path fillRule="evenodd" d="M12 7a1 1 0 110-2h5a1 1 0 011 1v5a1 1 0 11-2 0V8.414l-4.293 4.293a1 1 0 01-1.414 0L8 10.414l-4.293 4.293a1 1 0 01-1.414-1.414l5-5a1 1 0 011.414 0L11 10.586 14.586 7H12z" clipRule="evenodd" /></svg>;
    $[23] = t17;
  } else {
    t17 = $[23];
  }
  let t18;
  if ($[24] !== currentTestimonial.growth) {
    t18 = <div className="flex items-center gap-1 rounded-lg bg-emerald-50 px-3 py-1.5">{t17}<span className="text-[13px] font-bold text-emerald-600">{currentTestimonial.growth}</span></div>;
    $[24] = currentTestimonial.growth;
    $[25] = t18;
  } else {
    t18 = $[25];
  }
  let t19;
  if ($[26] !== t16 || $[27] !== t18) {
    t19 = <div className="flex items-center justify-between">{t16}{t18}</div>;
    $[26] = t16;
    $[27] = t18;
    $[28] = t19;
  } else {
    t19 = $[28];
  }
  let t20;
  if ($[29] !== currentTestimonial.id || $[30] !== t11 || $[31] !== t19) {
    t20 = <AnimatePresence mode="wait"><motion.div key={currentTestimonial.id} initial={t7} animate={t8} exit={t9} transition={t10} className="w-full rounded-[20px] bg-white p-6 shadow-2xl">{t11}{t19}</motion.div></AnimatePresence>;
    $[29] = currentTestimonial.id;
    $[30] = t11;
    $[31] = t19;
    $[32] = t20;
  } else {
    t20 = $[32];
  }
  let t21;
  if ($[33] !== currentTestimonialIndex) {
    t21 = TESTIMONIALS.map((_, index) => <button key={index} onClick={() => setCurrentTestimonialIndex(index)} className={`h-2 rounded-full transition-all duration-300 ${index === currentTestimonialIndex ? "w-6 bg-ink" : "w-2 bg-ink/25 hover:bg-ink/40"}`} aria-label={`View testimonial ${index + 1}`} />);
    $[33] = currentTestimonialIndex;
    $[34] = t21;
  } else {
    t21 = $[34];
  }
  let t22;
  if ($[35] !== t21) {
    t22 = <div className="mt-6 flex items-center gap-2">{t21}</div>;
    $[35] = t21;
    $[36] = t22;
  } else {
    t22 = $[36];
  }
  let t23;
  if ($[37] === Symbol.for("react.memo_cache_sentinel")) {
    t23 = <AvatarGroup className="justify-center" translate="-20%" side="top" sideOffset={8}>{AVATARS.map(_temp2)}<AvatarGroupYou size="md" tooltipText="You could be next!" href="/sign-up" variant="light" /></AvatarGroup>;
    $[37] = t23;
  } else {
    t23 = $[37];
  }
  let t24;
  if ($[38] === Symbol.for("react.memo_cache_sentinel")) {
    t24 = <div className="mt-10 flex flex-col items-center">{t23}<p className="mt-3 text-center text-[13px] font-medium text-ink/60">Join <span className="font-semibold text-ink/80">65,000+</span>{" creators & brands"}</p></div>;
    $[38] = t24;
  } else {
    t24 = $[38];
  }
  let t25;
  let t26;
  if ($[39] === Symbol.for("react.memo_cache_sentinel")) {
    t25 = <img src="https://fonts.gstatic.com/s/i/productlogos/googleg/v6/24px.svg" alt="Google" width={18} height={18} className="h-[18px] w-[18px]" />;
    t26 = <span className="text-[15px] font-bold text-ink">4.98</span>;
    $[39] = t25;
    $[40] = t26;
  } else {
    t25 = $[39];
    t26 = $[40];
  }
  let t27;
  if ($[41] === Symbol.for("react.memo_cache_sentinel")) {
    t27 = <div className="flex gap-0.5">{[...Array(5)].map(_temp3)}</div>;
    $[41] = t27;
  } else {
    t27 = $[41];
  }
  let t28;
  if ($[42] === Symbol.for("react.memo_cache_sentinel")) {
    t28 = <Anchor href="/outfame-reviews" className="mt-6 flex items-center gap-2 rounded-full bg-white px-4 py-2.5 shadow-md transition-all hover:shadow-lg">{t25}{t26}{t27}<span className="text-[13px] text-ink/60">from <span className="font-semibold text-ink/80">1,585+</span> reviews</span></Anchor>;
    $[42] = t28;
  } else {
    t28 = $[42];
  }
  let t29;
  if ($[43] === Symbol.for("react.memo_cache_sentinel")) {
    t29 = <a href="/blog/is-outfame-legit" target="_blank" rel="noopener" className="mt-3 text-[13px] font-medium text-ink/60 underline decoration-ink/20 underline-offset-4 transition-colors hover:text-ink"><T>Is <span translate="no">Outfame</span> legit? See the facts</T></a>;
    $[43] = t29;
  } else {
    t29 = $[43];
  }
  let t30;
  if ($[44] !== t20 || $[45] !== t22) {
    t30 = <div className="relative z-10 flex max-w-md flex-col items-center">{t20}{t22}{t24}{t28}{t29}</div>;
    $[44] = t20;
    $[45] = t22;
    $[46] = t30;
  } else {
    t30 = $[46];
  }
  let t31;
  if ($[47] === Symbol.for("react.memo_cache_sentinel")) {
    t31 = <div className="absolute inset-x-0 bottom-8"><LandingLogosCarousel isAuthPage={true} /></div>;
    $[47] = t31;
  } else {
    t31 = $[47];
  }
  let t32;
  if ($[48] !== t30) {
    t32 = <div className="relative flex h-full w-full flex-col items-center justify-center overflow-hidden rounded-[24px] px-10 xl:px-14" style={t5}>{t6}{t30}{t31}</div>;
    $[48] = t30;
    $[49] = t32;
  } else {
    t32 = $[49];
  }
  let t33;
  if ($[50] !== t32 || $[51] !== t4) {
    t33 = <div className={t4}>{t32}</div>;
    $[50] = t32;
    $[51] = t4;
    $[52] = t33;
  } else {
    t33 = $[52];
  }
  return t33;
}
function _temp3(__0, i) {
  return <svg key={i} className="h-4 w-4 fill-yellow-400" viewBox="0 0 20 20"><path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" /></svg>;
}
function _temp2(avatar, index_0) {
  return <AvatarGroupItem key={index_0}><SimpleAvatar src={avatar.src} alt={avatar.name} fallback={avatar.fallback} size="md" className="ring-white shadow-lg" /><AvatarGroupTooltip><p className="font-medium">{avatar.name}</p><p className="text-[14px] text-emerald-400">{avatar.growth}</p></AvatarGroupTooltip></AvatarGroupItem>;
}
function _temp(prev) {
  return (prev + 1) % TESTIMONIALS.length;
}